import deepEquals from "fast-deep-equal";
import React, { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { emptyRevocableTrust } from "src/EstateFlowchart/utils/DefUtils";
import { useAppDispatch, useAppSelector } from "src/store/hooks";
import { selectReleaseToggles } from "../../../ReleaseToggles/releaseTogglesSlice";
import { RouteWithIdAndFlowchartId } from "../../../routes/types";
import { MemberGroup } from "src/ClientManagement/models/InvestorGroupType";
import useAnalytics from "../../../utils/useAnalytics";
import { estateFlowchartApiClient, ResponseStatus } from "../../client/EstateFlowchartApiClient";
import { EstateCardViewModeType } from "../../components/EstateCard/EstateCard";
import Form from "../../components/Form/Form";
import { getNavItemPage } from "../../components/FormNav/FormNavUtils";
import { NavItem } from "../../components/FormNav/NavItem";
import { ADDITIONAL_DISTRIBUTIONS_ID, BENEFICIARIES_AT_FIRST_DEATH_ID, REMAINDER_BENEFICIARIES_ID } from "../../config/FormPages";
import { RevocableTrustFormPages } from "../../config/RevocableTrustFormPages";
import {
    AdditionalDistributionLayer,
    EstateFlowchartState,
    MemberType,
    RevocableTrust,
    RevocableTrustModalInputs,
    Trustee
} from "../../model/EstateFlowchartTypes";
import {
    familyMembersSelector,
    getEstateFlowchartsSelector,
    selectedFlowchartIdSelector,
    setGenericErrorModal,
    setSelectedFlowchartId,
    updateEstateFlowchart
} from "../../slices/EstateFlowChartSlice";
import { resolveTriggerMemberName } from "../../utils/DefUtils";
import { navItemSelector, setSaveClicked } from "../../slices/NavigationSlice";
import { validateForm } from "../../validation/form/RevocableTrustFormValidator";
import { ValidationError } from "../../validation/ValidationTypes";
import { EVENT_NAME_MAX_LENGTH } from "../../validation/ValidationUtils";
import { EstateItemType } from "../EstateItemType";
import RevocableTrustContent from "./RevocableTrustContent";

const getAdditionalDistributionTriggerFlags = (additionalDistributions?: AdditionalDistributionLayer) => ({
    hasTriggerType: !!additionalDistributions?.triggerEventType,
    hasMember: !!additionalDistributions?.triggerMemberId || !!additionalDistributions?.triggerMemberName,
    hasAge: additionalDistributions?.triggerAge !== null && additionalDistributions?.triggerAge !== undefined,
    hasEventText: !!additionalDistributions?.triggerEventText?.trim()
});

const getAdditionalDistributionsNavLabel = (
    additionalDistributions: AdditionalDistributionLayer | undefined,
    rawFamilyMembers: MemberGroup
): string | undefined => {
    if (!additionalDistributions) {
        return undefined;
    }

    const {hasTriggerType, hasMember, hasAge, hasEventText} = getAdditionalDistributionTriggerFlags(additionalDistributions);
    const hasValidEventTextLength = (additionalDistributions.triggerEventText?.trim()?.length ?? 0) <= EVENT_NAME_MAX_LENGTH;

    const isComplete =
        hasTriggerType &&
        ((additionalDistributions.triggerEventType === "DEATH" && hasMember) ||
            (additionalDistributions.triggerEventType === "AGE_ATTAINMENT" && hasMember && hasAge) ||
            (additionalDistributions.triggerEventType === "OTHER" && hasEventText && hasValidEventTextLength));

    if (!isComplete) {
        return undefined;
    }

    const memberName = resolveTriggerMemberName(
        rawFamilyMembers,
        additionalDistributions.triggerMemberId,
        additionalDistributions.triggerMemberName
    );

    switch (additionalDistributions.triggerEventType) {
        case "DEATH":
            return memberName ? `Beneficiaries at ${memberName}'s Death` : "Additional Distribution";
        case "AGE_ATTAINMENT":
            return memberName && additionalDistributions.triggerAge !== null && additionalDistributions.triggerAge !== undefined
                ? `Beneficiaries After ${memberName} Turns ${additionalDistributions.triggerAge}`
                : "Additional Distribution";
        case "OTHER":
            return `Beneficiaries After ${additionalDistributions.triggerEventText?.trim() ?? "Additional Distribution"}`;
        default:
            return "Additional Distribution";
    }
};

const hasAdditionalDistributionInput = (additionalDistributions?: AdditionalDistributionLayer): boolean => {
    if (!additionalDistributions) {
        return false;
    }

    const {hasTriggerType, hasMember, hasAge, hasEventText} = getAdditionalDistributionTriggerFlags(additionalDistributions);
    return hasTriggerType || hasMember || hasAge || hasEventText;
};

const sanitizeRevocableTrust = (revocableTrust: RevocableTrust): RevocableTrust => {
    const additionalDistributions = revocableTrust.beneficiaries?.additionalDistributions;
    if (hasAdditionalDistributionInput(additionalDistributions)) {
        return revocableTrust;
    }

    if (!revocableTrust.beneficiaries) {
        return revocableTrust;
    }

    return {
        ...revocableTrust,
        beneficiaries: {
            ...revocableTrust.beneficiaries,
            additionalDistributions: undefined
        }
    };
};

const buildRevocableTrustPageConfig = (
    basePages: NavItem[],
    hasBeneficiariesAtFirstDeath: boolean,
    enableAdditionalDistributionLayer: boolean | undefined,
    additionalDistributionsLabel: string | undefined
): NavItem[] => {
    let pages = basePages;
    if (!hasBeneficiariesAtFirstDeath) {
        pages = pages.filter((page) => page.id !== BENEFICIARIES_AT_FIRST_DEATH_ID);
    }
    if (!enableAdditionalDistributionLayer || !additionalDistributionsLabel) {
        pages = pages.filter((page) => page.id !== ADDITIONAL_DISTRIBUTIONS_ID);
    }

    return pages.map((page, index) => {
        if (page.id === ADDITIONAL_DISTRIBUTIONS_ID) {
            return {
                ...page,
                index,
                displayName: additionalDistributionsLabel ?? page.displayName
            } as NavItem;
        }
        return {
            ...page,
            index
        } as NavItem;
    });
};

const RevocableTrustForm = () => {
    const location = useLocation();
    const dispatch = useAppDispatch();
    const estateFlowchartsInState = useAppSelector(getEstateFlowchartsSelector);
    const selectedFlowchartId = useAppSelector(selectedFlowchartIdSelector);
    const releaseToggles = useAppSelector(selectReleaseToggles);
    const rawFamilyMembers = useAppSelector(familyMembersSelector);

    const {id, flowchartId, trustId} = useParams<RouteWithIdAndFlowchartId>();
    const [validationErrors, setValidationErrors] = useState<ValidationError[]>();
    const [selectedFlowchart, setSelectedFlowchart] = useState<EstateFlowchartState>();
    const [revocableTrust, setRevocableTrust] = useState<RevocableTrust>(emptyRevocableTrust);

    const [committedAdditionalDistributions, setCommittedAdditionalDistributions] = useState<AdditionalDistributionLayer | undefined>(
        undefined
    );

    const [formHasBeenEdited, setFormHasBeenEdited] = useState(false);
    const activeNavItem = useAppSelector(navItemSelector);
    const [pageConfig, setPageConfig] = useState(RevocableTrustFormPages);
    const [linkConfig, setLinkConfig] = useState({});
    const flowcharts = useAppSelector(getEstateFlowchartsSelector);
    const [initialDefaultModalInputs, setInitialDefaultModalInputs] = useState<RevocableTrustModalInputs>({} as RevocableTrustModalInputs);

    const showBeneficiariesAtFirstDeath = (flowchartModelData: RevocableTrustModalInputs) => {
        if (flowchartModelData?.estateItem) {
            return flowchartModelData?.distributionStructure !== "Split among remainder beneficiaries";
        } else {
            return false;
        }
    }

    useEffect(() => {
        const selectedFlowchart = estateFlowchartsInState.find((flowchart) => flowchart.flowchartId === selectedFlowchartId);
        setSelectedFlowchart(selectedFlowchart);
        if (selectedFlowchart?.revocableTrust) {
            setRevocableTrust({
                ...selectedFlowchart.revocableTrust,
                flowchartName: selectedFlowchart.flowchartName ?? ""
            });
            setCommittedAdditionalDistributions(selectedFlowchart.revocableTrust?.beneficiaries?.additionalDistributions);
        }
    }, [selectedFlowchartId]);

    useEffect(() => {
        const flowchartModelData = location.state;
        if (flowchartModelData?.estateItem) {
            setInitialDefaultModalInputs(flowchartModelData);
            setRevocableTrust({
                ...revocableTrust,
                flowchartName: flowchartModelData?.flowchartName || selectedFlowchart?.flowchartName,
                grantors: flowchartModelData?.revTrustType.map((member: Trustee) => ({
                    trustId: '',
                    memberType: MemberType.Grantor,
                    familyMember: true,
                    customName: '',
                    memberId: member.memberId
                })),
                trustees: flowchartModelData?.revTrustType.map((member: Trustee) => ({
                    trustId: '',
                    memberType: MemberType.Trustee,
                    familyMember: true,
                    customName: '',
                    memberId: member.memberId
                })),
                assumedFirstDeath: location.state.assumedFirstDeath,
                hasBeneficiariesAtFirstDeath: showBeneficiariesAtFirstDeath(flowchartModelData)
            })
        }
    }, [selectedFlowchart]);

    useEffect(() => {
        // Configure Navigation Jump Links
        let revocableTrustLinkConfig: any = {};
        revocableTrustLinkConfig[BENEFICIARIES_AT_FIRST_DEATH_ID] = revocableTrust?.beneficiaries?.firstDeathDistributions;
        const remainderDistributions = revocableTrust?.beneficiaries?.remainderDistributions ?? [];
        const additionalDistributions = revocableTrust?.beneficiaries?.additionalDistributions;
        const labelSource = committedAdditionalDistributions ?? additionalDistributions;
        const additionalDistributionsLabel = getAdditionalDistributionsNavLabel(labelSource, rawFamilyMembers);

        revocableTrustLinkConfig[REMAINDER_BENEFICIARIES_ID] = remainderDistributions;
        revocableTrustLinkConfig[ADDITIONAL_DISTRIBUTIONS_ID] = additionalDistributions?.distributions ?? [];
        setLinkConfig(revocableTrustLinkConfig);
        setPageConfig(buildRevocableTrustPageConfig(
            RevocableTrustFormPages,
            revocableTrust.hasBeneficiariesAtFirstDeath,
            releaseToggles?.enableAdditionalDistributionLayer,
            additionalDistributionsLabel
        ));

        const defaultRevocableTrust: RevocableTrust = {
            ...emptyRevocableTrust,
            flowchartName: initialDefaultModalInputs.flowchartName ?? selectedFlowchart?.flowchartName ?? "",
            assumedFirstDeath: initialDefaultModalInputs.assumedFirstDeath ?? "",
            grantors: initialDefaultModalInputs?.revTrustType?.map((member: Trustee) => ({
                trustId: '',
                memberType: MemberType.Grantor,
                familyMember: true,
                customName: '',
                memberId: member.memberId
            })) || [],
            trustees: initialDefaultModalInputs?.revTrustType?.map((member: Trustee) => ({
                trustId: '',
                memberType: MemberType.Trustee,
                familyMember: true,
                customName: '',
                memberId: member.memberId
            })) || [],
            hasBeneficiariesAtFirstDeath: showBeneficiariesAtFirstDeath(initialDefaultModalInputs),
        };

        // Check if form has been edited
        const initialRevocableTrust: RevocableTrust = estateFlowchartsInState.find(flowchart =>
            flowchart.flowchartId === flowchartId)?.revocableTrust || defaultRevocableTrust;
        let revocableTrustToCompare = revocableTrust;
        if (flowchartId === "new") {
            revocableTrustToCompare = {...revocableTrust, flowchartName: ""};
        }
        const hasBeenEdited: boolean = !deepEquals(initialRevocableTrust,revocableTrustToCompare);
        setFormHasBeenEdited(hasBeenEdited);
    }, [revocableTrust, committedAdditionalDistributions, rawFamilyMembers, releaseToggles?.enableAdditionalDistributionLayer, trustId]);

    useAnalytics(
        "DEF_RevocableTrust_Form",
        "DEF_RevocableTrust_Form_TimeSpent",
    );

    const setCommunicationError = (errorStatus: any) => {
        dispatch(setGenericErrorModal({
            isOpen: true,
            operationId: errorStatus.traceId
        }));
    }

    const checkFormIsValid = () => {
        const pageId = getNavItemPage(activeNavItem)?.id;
        const validations = validateForm(pageId, revocableTrust, releaseToggles?.enableEstateFlowchartMVPRelease ?? false, flowcharts, flowchartId);
        setValidationErrors(validations.errors)
        return !validations.formHasErrors;
    }

    const createRevocableTrustToNewFlowchart = async (): Promise<boolean> => {
        const sanitizedRevocableTrust = sanitizeRevocableTrust(revocableTrust);
        const estateFlowchart: EstateFlowchartState = {
            flowchartId: undefined,
            flowchartName: sanitizedRevocableTrust.flowchartName,
            revocableTrust: sanitizedRevocableTrust,
            viewMode: EstateCardViewModeType.SIMPLE,
            estateTaxDueTypes: []
        };

        const res = await estateFlowchartApiClient.createFlowchart(id, estateFlowchart);
        if (res.isSuccessStatusCode) {
            dispatch(updateEstateFlowchart(res.data));
            dispatch(setSelectedFlowchartId(res.data.flowchartId));
            setRevocableTrust(res.data.revocableTrust ?? {} as RevocableTrust);
            return true;
        } else if (releaseToggles?.enableEstateFlowchartMVPRelease) {
            setCommunicationError(res);
        }

        return false;
    };

    const addRevocableTrustToExistingFlowchart = async (): Promise<boolean> => {
        if (selectedFlowchartId) {
            const sanitizedRevocableTrust = sanitizeRevocableTrust(revocableTrust);
            const res = await estateFlowchartApiClient.createRevocableTrust(id, flowchartId, sanitizedRevocableTrust);
            console.log(res)
            if (res.isSuccessStatusCode) {
                setRevocableTrust((res as ResponseStatus).data as RevocableTrust);
                dispatch(updateEstateFlowchart({
                    ...selectedFlowchart,
                    flowchartName: (res as ResponseStatus).data.flowchartName,
                    revocableTrust: (res as ResponseStatus).data ?? {}
                } as EstateFlowchartState));
                return true;
            } else if (releaseToggles?.enableEstateFlowchartMVPRelease) {
                setCommunicationError(res);
            }
        }

        return false;
    };

    const handleEditRevocableTrust = async (): Promise<boolean> => {
        if (formHasBeenEdited) {
            const sanitizedRevocableTrust = sanitizeRevocableTrust(revocableTrust);
            const res = await estateFlowchartApiClient.editRevocableTrust(id, flowchartId, sanitizedRevocableTrust);
            if (res.isSuccessStatusCode) {
                dispatch(updateEstateFlowchart({
                    ...selectedFlowchart,
                    flowchartName: (res as ResponseStatus).data.flowchartName,
                    revocableTrust: (res as ResponseStatus).data ?? {}
                } as EstateFlowchartState));
                return true;
            } else if (releaseToggles?.enableEstateFlowchartMVPRelease) {
                setCommunicationError(res);
            }
        }

        return false;
    };

    const saveForm = (): Promise<boolean> => {
        dispatch(setSaveClicked(true));
        let handleSave: () => Promise<boolean>;
        if (flowchartId === "new" && trustId === "new") {
            handleSave = createRevocableTrustToNewFlowchart;
        } else if (trustId === "new") {
            handleSave = addRevocableTrustToExistingFlowchart; // This should be changed to new endpoint to Add revTrust to existing flowchart
        } else {
            handleSave = handleEditRevocableTrust;
        }
        return handleSave();
    };

    return (
        <Form estateItem={revocableTrust}
              estateItemType={EstateItemType.REVOCABLE_TRUST}
              title={"Revocable Trust"}
              subHeader={revocableTrust?.flowchartName || undefined}
              pageConfig={pageConfig}
              linkConfig={linkConfig}
              isFormChanged={formHasBeenEdited}
              validate={checkFormIsValid}
              saveForm={saveForm}
              validationErrors={validationErrors ?? []}>

            <RevocableTrustContent revocableTrust={revocableTrust}
                                   pageConfig={pageConfig}
                                   updateRevocableTrust={(nextRevocableTrust) => {

                                       const prevDraft = revocableTrust?.beneficiaries?.additionalDistributions;
                                       const nextDraft = nextRevocableTrust?.beneficiaries?.additionalDistributions;

                                       const triggerTypeChanged =
                                           (prevDraft?.triggerEventType ?? "") !== (nextDraft?.triggerEventType ?? "");

                                       if (!triggerTypeChanged && nextDraft) {
                                           const { hasTriggerType, hasMember, hasAge, hasEventText } =
                                               getAdditionalDistributionTriggerFlags(nextDraft);
                                           const hasValidEventTextLength =
                                               (nextDraft.triggerEventText?.trim()?.length ?? 0) <= EVENT_NAME_MAX_LENGTH;

                                           const isComplete =
                                               hasTriggerType &&
                                               ((nextDraft.triggerEventType === "DEATH" && hasMember) ||
                                                   (nextDraft.triggerEventType === "AGE_ATTAINMENT" && hasMember && hasAge) ||
                                                   (nextDraft.triggerEventType === "OTHER" && hasEventText && hasValidEventTextLength));

                                           if (isComplete) {
                                               setCommittedAdditionalDistributions(nextDraft);
                                           }
                                       }

                                       if (!nextDraft) {
                                           setCommittedAdditionalDistributions(undefined);
                                       }

                                       setRevocableTrust(nextRevocableTrust);
                                   }}
                                   saveForm={saveForm}/>
        </Form>
    );
};

export default RevocableTrustForm;
